import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Theme,
  IconButton,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import Colors from '../../utils/color';

export enum UnresolvedItemVariant {
  NOT_ON_MENU = 'notOnMenu',
  INVALID_OPTION = 'invalidOption',
}

interface Props {
  variant: UnresolvedItemVariant;
  title: string;
  diagnosticLabel: string;
}

const BACKGROUND_COLOR: Record<UnresolvedItemVariant, string> = {
  [UnresolvedItemVariant.NOT_ON_MENU]: Colors.apricot,
  [UnresolvedItemVariant.INVALID_OPTION]: Colors.beeswax,
};

const useStyles = makeStyles(({ spacing }: Theme) => ({
  container: (props: { variant: UnresolvedItemVariant }) => ({
    marginTop: spacing(2),
    backgroundColor: `${BACKGROUND_COLOR[props.variant]} !important`,
  }),
  content: {
    paddingBottom: `${spacing(0.5)} !important`,
  },
  mainItemname: {
    '& h6': {
      lineHeight: '1.57 !important',
      fontSize: `${spacing(1.5)} !important`,
      fontWeight: 'bold !important',
    },
    paddingBottom: spacing(1),
  },
  diagnosticLabel: {
    fontStyle: 'italic',
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      cursor: 'pointer',
    },
  },
  quantity: {
    textAlign: 'center',
    display: 'inline-block',
    minWidth: '40px !important',
    paddingRight: '2px !important',
    paddingLeft: '0px !important',
  },
}));

/**
 * Unified diagnostic card for unresolved items — covers both "not on menu"
 * and "invalid option" variants. Display-only; dismissal is handled by the
 * parent panel's "Dismiss & Repeat" button.
 */
export default function UnresolvedItemCard({
  variant,
  title,
  diagnosticLabel,
}: Props) {
  const classes = useStyles({ variant });

  return (
    <Card elevation={3} className={classes.container}>
      <CardContent className={classes.content}>
        <div className={classes.mainItemname}>
          <Typography variant="subtitle2">{title}</Typography>
          <Typography variant="subtitle2" className={classes.diagnosticLabel}>
            ({diagnosticLabel})
          </Typography>
        </div>
        {variant === UnresolvedItemVariant.NOT_ON_MENU && (
          <div className={classes.actionsWrapper}>
            <div>
              <IconButton size="small" disabled={true}>
                <RemoveIcon />
              </IconButton>
              <span className={classes.quantity}>1</span>
              <IconButton size="small" disabled={true}>
                <AddIcon />
              </IconButton>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
